import { useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Check, Loader2 } from "lucide-react";
import { TemperMark } from "../components/Logo";
import Seo from "../components/Seo";

const EASE = [0.16, 1, 0.3, 1];

const DIMENSIONS = [
  {
    letter: "T",
    word: "Truth",
    questions: [
      "I actively seek out feedback, even when it's hard to hear.",
      "My team hears hard news from me first — not through the grapevine.",
    ],
  },
  {
    letter: "E",
    word: "Empathy",
    questions: [
      "I know what's going on in my people's lives beyond the work.",
      "I adapt how I communicate to each person on my team.",
    ],
  },
  {
    letter: "M",
    word: "Mastery",
    questions: [
      "I hold myself to a higher standard than I expect from others.",
      "I invest in my own growth as deliberately as I invest in my team's.",
    ],
  },
  {
    letter: "P",
    word: "Purpose",
    questions: [
      "My team can explain why our work matters beyond the numbers.",
      "I connect individual roles to the larger mission clearly and often.",
    ],
  },
  {
    letter: "E",
    word: "Endurance",
    questions: [
      "I stay composed and effective under sustained pressure.",
      "Setbacks sharpen my focus rather than drain it.",
    ],
  },
  {
    letter: "R",
    word: "Resolve",
    questions: [
      "I make hard decisions promptly, even with incomplete information.",
      "I address conflict directly instead of letting it smolder.",
    ],
  },
];

function Scale({ value, onChange, prefix = "q" }) {
  return (
    <div>
      <div className="flex gap-2 md:gap-3">
        {[1, 2, 3, 4, 5].map((v) => (
          <button
            key={v}
            type="button"
            data-testid={`${prefix}-scale-${v}`}
            aria-label={`Rate ${v} of 5`}
            onClick={() => onChange(v)}
            className={`h-12 w-12 border font-mono text-sm transition-colors duration-200 md:h-14 md:w-14 ${
              value === v
                ? "border-ember bg-ember text-paper"
                : "border-ink/25 text-ink/60 hover:border-ink"
            }`}
          >
            {v}
          </button>
        ))}
      </div>
      <div className="mt-3 flex justify-between font-mono text-[10px] uppercase tracking-[0.18em] text-ink/40">
        <span>Rarely me</span>
        <span>Always me</span>
      </div>
    </div>
  );
}

export default function Questionnaire() {
  const [step, setStep] = useState(0);
  const [info, setInfo] = useState({ name: "", email: "", role: "", organization: "" });
  const [answers, setAnswers] = useState({});
  const [phase, setPhase] = useState("form");
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);

  const emailOk = /\S+@\S+\.\S+/.test(info.email);
  const infoOk = info.name.trim().length > 1 && emailOk;

  const dimAnswered = (di) =>
    DIMENSIONS[di].questions.every((_, qi) => answers[`${di}-${qi}`]);

  const computeScores = () => {
    const scores = {};
    DIMENSIONS.forEach((d, di) => {
      const a = d.questions.map((_, qi) => answers[`${di}-${qi}`] || 3);
      scores[d.word] = Math.round(((a[0] + a[1]) / 2 / 5) * 100);
    });
    const entries = Object.entries(scores);
    const overall = Math.round(entries.reduce((s, [, v]) => s + v, 0) / entries.length);
    const strong = entries.reduce((a, b) => (b[1] > a[1] ? b : a))[0];
    const weak = entries.reduce((a, b) => (b[1] < a[1] ? b : a))[0];
    return { scores, overall, strong, weak };
  };

  const handleSubmit = async () => {
    setPhase("saving");
    setError("");
    const data = computeScores();
    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/questionnaire`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: info.name,
          email: info.email,
          role: info.role || null,
          organization: info.organization || null,
          answers,
          scores: data.scores,
          overall: data.overall,
        }),
      }).then((r) => {
        if (!r.ok) throw new Error("failed");
        return r.json();
      });
      setResult(data);
      setPhase("result");
    } catch {
      setPhase("form");
      setError("Couldn't save your results — check your connection and try again.");
    }
  };

  const reset = () => {
    setStep(0);
    setAnswers({});
    setResult(null);
    setPhase("form");
    setError("");
  };

  if (phase === "result" && result) {
    return (
      <section data-testid="questionnaire-result" className="min-h-screen pt-32 pb-24 md:pt-44">
        <Seo
          title="Free Leadership Assessment — The TEMPER Profile"
          description="Take the TEMPER Profile, a free leadership assessment scoring truth, empathy, mastery, purpose, endurance, and resolve. Twelve statements, four minutes."
          path="/temper-profile"
        />
        <div className="mx-auto max-w-3xl px-6 md:px-10">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: EASE }}>
            <p className="eyebrow flex items-center gap-3 text-ember">
              <TemperMark className="h-3 w-3" /> Your TEMPER Profile
            </p>
            <h1 className="mt-6 font-display text-5xl font-black tracking-tight md:text-7xl">
              {result.overall}
              <span className="text-2xl text-steel md:text-3xl"> /100</span>
            </h1>
            <p className="mt-2 font-mono text-[11px] uppercase tracking-[0.22em] text-ink/50">
              Overall temper — {info.name.split(" ")[0]}, how you lead under pressure
            </p>
          </motion.div>

          <div className="mt-14 space-y-6">
            {Object.entries(result.scores).map(([word, v], i) => (
              <div key={word} data-testid={`result-bar-${word.toLowerCase()}`}>
                <div className="flex items-baseline justify-between font-mono text-[11px] uppercase tracking-[0.2em]">
                  <span className="text-ink/70">{word}</span>
                  <span className={v === result.strong ? "text-ember" : "text-ink/50"}>{v}</span>
                </div>
                <div className="mt-2 h-1.5 w-full bg-ink/10">
                  <motion.div
                    className={`h-full ${v === result.strong ? "bg-ember" : "bg-ink"}`}
                    initial={{ width: 0 }}
                    animate={{ width: `${v}%` }}
                    transition={{ duration: 0.9, delay: 0.2 + i * 0.1, ease: EASE }}
                  />
                </div>
              </div>
            ))}
          </div>

          <div className="mt-14 grid gap-6 md:grid-cols-2">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.9, duration: 0.7, ease: EASE }}
              className="border border-ember/60 p-8"
            >
              <p className="eyebrow text-ember">Sharpest edge</p>
              <h3 className="mt-3 font-display text-3xl font-extrabold tracking-tight">{result.strong}</h3>
              <p className="mt-3 leading-relaxed text-steel">
                This is where you're strongest under pressure. Guard it — and let others lean on it.
              </p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1, duration: 0.7, ease: EASE }}
              className="border border-line p-8"
            >
              <p className="eyebrow text-ink/50">Your temper edge</p>
              <h3 className="mt-3 font-display text-3xl font-extrabold tracking-tight">{result.weak}</h3>
              <p className="mt-3 leading-relaxed text-steel">
                This is where deliberate work pays the most. It's the dimension to bring to a
                first conversation.
              </p>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.2, duration: 0.7 }}
            className="mt-14 flex flex-wrap items-center gap-4"
          >
            <Link to="/contact" data-testid="result-contact-cta" className="btn-ink">
              Bring your profile to a conversation <ArrowRight className="h-4 w-4" />
            </Link>
            <button onClick={reset} data-testid="result-retake" className="btn-outline">
              Retake
            </button>
          </motion.div>
          <p className="mt-8 font-mono text-[10px] uppercase tracking-[0.2em] text-ink/40">
            Results saved — Eric reviews every profile personally.
          </p>
        </div>
      </section>
    );
  }

  const stepDone = step === 0 ? infoOk : dimAnswered(step - 1);

  return (
    <section data-testid="questionnaire" className="relative min-h-screen overflow-hidden pt-32 pb-24 md:pt-44">
      <Seo
        title="Free Leadership Assessment — The TEMPER Profile"
        description="Take the TEMPER Profile, a free leadership assessment scoring truth, empathy, mastery, purpose, endurance, and resolve. Twelve statements, four minutes."
        path="/temper-profile"
      />
      {step > 0 && (
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -right-10 top-24 select-none font-display text-[16rem] font-black leading-none text-ink/[0.04] md:text-[26rem]"
        >
          {DIMENSIONS[step - 1].letter}
        </div>
      )}
      <div className="mx-auto w-full max-w-3xl px-6 md:px-10">
        <div className="flex items-center justify-between font-mono text-[11px] uppercase tracking-[0.2em] text-ink/50">
          <span>The TEMPER Profile</span>
          <span data-testid="questionnaire-progress">
            {step === 0 ? "Start" : `0${step} / 06`}
          </span>
        </div>
        <div className="mt-4 h-px w-full bg-ink/10">
          <motion.div
            className="h-full bg-ember"
            animate={{ width: `${((step + (stepDone ? 1 : 0)) / 7) * 100}%` }}
            transition={{ duration: 0.5, ease: EASE }}
          />
        </div>

        <AnimatePresence mode="wait">
          {step === 0 ? (
            <motion.div
              key="intro"
              initial={{ opacity: 0, x: 48 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -48 }}
              transition={{ duration: 0.45, ease: EASE }}
              className="mt-14"
            >
              <h1 className="font-display text-4xl font-black leading-[1.02] tracking-tight md:text-6xl">
                Twelve statements. Six dimensions.{" "}
                <span className="text-ember">One honest read.</span>
              </h1>
              <p className="mt-6 max-w-xl leading-relaxed text-steel">
                Answer as you actually are under pressure — not as you hope to be. Your
                results are saved and reviewed by Eric personally.
              </p>
              <div className="mt-12 grid gap-8 md:grid-cols-2">
                <label className="block">
                  <span className="eyebrow text-ink/50">Name *</span>
                  <input
                    data-testid="questionnaire-info-name"
                    className="field-input mt-2"
                    placeholder="Your name"
                    value={info.name}
                    onChange={(e) => setInfo({ ...info, name: e.target.value })}
                  />
                </label>
                <label className="block">
                  <span className="eyebrow text-ink/50">Work email *</span>
                  <input
                    data-testid="questionnaire-info-email"
                    type="email"
                    className="field-input mt-2"
                    placeholder="you@company.com"
                    value={info.email}
                    onChange={(e) => setInfo({ ...info, email: e.target.value })}
                  />
                </label>
                <label className="block">
                  <span className="eyebrow text-ink/50">Role</span>
                  <input
                    data-testid="questionnaire-info-role"
                    className="field-input mt-2"
                    placeholder="e.g. Director of Operations"
                    value={info.role}
                    onChange={(e) => setInfo({ ...info, role: e.target.value })}
                  />
                </label>
                <label className="block">
                  <span className="eyebrow text-ink/50">Organization</span>
                  <input
                    data-testid="questionnaire-info-organization"
                    className="field-input mt-2"
                    placeholder="Company or team"
                    value={info.organization}
                    onChange={(e) => setInfo({ ...info, organization: e.target.value })}
                  />
                </label>
              </div>
              <div className="mt-12">
                <button
                  data-testid="questionnaire-start"
                  disabled={!infoOk}
                  onClick={() => setStep(1)}
                  className={`btn ${infoOk ? "btn-ember" : "cursor-not-allowed bg-ink/10 text-ink/40"}`}
                >
                  Begin — about 4 minutes <ArrowRight className="h-4 w-4" />
                </button>
              </div>
            </motion.div>
          ) : phase === "saving" ? (
            <motion.div
              key="saving"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="mt-24 flex flex-col items-center gap-6 text-center"
            >
              <Loader2 className="h-8 w-8 animate-spin text-ember" data-testid="questionnaire-saving" />
              <p className="font-display text-3xl font-extrabold tracking-tight">
                Tempering your results…
              </p>
            </motion.div>
          ) : (
            <motion.div
              key={`dim-${step}`}
              initial={{ opacity: 0, x: 48 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -48 }}
              transition={{ duration: 0.45, ease: EASE }}
              className="mt-14"
            >
              <p className="eyebrow text-ember">
                Dimension {step} of 6 — {DIMENSIONS[step - 1].letter} · {DIMENSIONS[step - 1].word}
              </p>
              <div className="mt-10 space-y-14">
                {DIMENSIONS[step - 1].questions.map((q, qi) => (
                  <div key={qi}>
                    <p
                      data-testid={`question-${step - 1}-${qi}`}
                      className="font-display text-2xl font-bold leading-snug tracking-tight md:text-3xl"
                    >
                      <span className="mr-4 font-mono text-sm text-ember">0{qi + 1}</span>
                      {q}
                    </p>
                    <div className="mt-6">
                      <Scale
                        prefix={`q${step - 1}-${qi}`}
                        value={answers[`${step - 1}-${qi}`] || 0}
                        onChange={(v) => setAnswers({ ...answers, [`${step - 1}-${qi}`]: v })}
                      />
                    </div>
                  </div>
                ))}
              </div>
              {error && (
                <p data-testid="questionnaire-error" className="mt-8 text-sm text-forge">
                  {error}
                </p>
              )}
              <div className="mt-14 flex items-center justify-between">
                <button
                  data-testid="questionnaire-back"
                  onClick={() => setStep(step - 1)}
                  className="arrow-link text-ink/60 hover:text-ink"
                >
                  <ArrowLeft className="h-4 w-4" /> Back
                </button>
                {step < 6 ? (
                  <button
                    data-testid="questionnaire-next"
                    disabled={!dimAnswered(step - 1)}
                    onClick={() => setStep(step + 1)}
                    title={dimAnswered(step - 1) ? "" : "Rate both statements to continue"}
                    className={`btn ${dimAnswered(step - 1) ? "btn-ember" : "cursor-not-allowed bg-ink/10 text-ink/40"}`}
                  >
                    Continue <ArrowRight className="h-4 w-4" />
                  </button>
                ) : (
                  <button data-testid="questionnaire-submit" onClick={handleSubmit} className="btn-ember">
                    See my profile <Check className="h-4 w-4" />
                  </button>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
}
